/**
 * CityVenuesIndexPage — public, SEO-friendly directory of every active
 * venue in a city (e.g. `/events/denver/venues`). Mirrors the visual
 * grammar of the venue marketplace but optimised for indexability:
 * server-rendered card grid, `ItemList` + breadcrumb JSON-LD, canonical
 * URL anchored to the city path, and "X upcoming events" badges so the
 * page degrades gracefully when no events are on the calendar.
 *
 * The "Series" indicator is rendered on each card when the venue hosts
 * a known recurring series (e.g. weekly tasting). For now we surface
 * the count of upcoming events as a proxy; the API already exposes the
 * full series payload on the per-venue profile page.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, MapPin, Users, ChevronRight, Loader2, Sparkles, Calendar, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { breadcrumbLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const capitalize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export default function CityVenuesIndexPage() {
  const { city = 'denver' } = useParams();
  const [searchParams] = useSearchParams();
  // Filter params come from in-funnel links on the venue profile page —
  // category kicker, neighborhood, vibe tags. All optional; absent →
  // full city directory.
  const typeFilter = searchParams.get('type') || '';
  const neighborhoodFilter = searchParams.get('neighborhood') || '';
  const tagFilter = searchParams.get('tag') || '';

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const cityName = useMemo(() => capitalize(city), [city]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/cities/${city}/venues`, {
      params: {
        limit: 60,
        type: typeFilter || undefined,
        neighborhood: neighborhoodFilter || undefined,
        tag: tagFilter || undefined,
      },
    })
      .then((r) => { if (!cancelled) setVenues(r.data?.venues || []); })
      .catch(() => { if (!cancelled) setVenues([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city, typeFilter, neighborhoodFilter, tagFilter]);

  const canonicalUrl = `${SITE_URL}/${city}/venues`;
  const totalActive = venues.length;
  const totalUpcoming = venues.reduce((sum, v) => sum + (v.upcoming_events_count || 0), 0);

  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Events', url: '/events' },
    { name: cityName, url: `/events/${city}` },
    { name: 'Venues', url: `/${city}/venues` },
  ]);

  // ItemList LD — feeds Google's rich results so the venue directory
  // can surface as a list snippet for "<city> event venues" queries.
  const itemListLD = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Event venues in ${cityName}`,
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    numberOfItems: venues.length,
    itemListElement: venues.map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}${v.url}`,
      item: {
        '@type': 'EventVenue',
        name: v.name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: v.address || undefined,
          addressLocality: v.city,
          addressRegion: v.state || undefined,
          postalCode: v.zip_code || undefined,
        },
        image: v.cover_photo || undefined,
        maximumAttendeeCapacity: v.capacity_max || undefined,
      },
    })),
  }), [venues, cityName]);

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="city-venues-index">
      <SEO
        title={`Event Venues in ${cityName} — Book ${cityName} Spaces & See Upcoming Events | Eventism`}
        description={`Browse ${totalActive || 'every'} event venue in ${cityName}: capacity, amenities, photos and upcoming events on the calendar. Instant booking and SEO-listed for organizers and partner brands.`}
        canonical={canonicalUrl}
        url={canonicalUrl}
        keywords={[
          `${cityName} event venues`,
          `${cityName} venue rental`,
          `event spaces ${cityName}`,
          `${cityName} wedding venues`,
          `${cityName} party venues`,
          `${cityName} corporate event venues`,
        ]}
        structuredData={[breadcrumb, itemListLD]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6" data-testid="city-venues-breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/events" className="hover:text-foreground">Events</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/events/${city}`} className="hover:text-foreground">{cityName}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">Venues</span>
        </nav>

        {/* Hero */}
        <section className="space-y-4 mb-8" data-testid="city-venues-hero">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Venue directory
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]" data-testid="city-venues-h1">
            Event venues in{' '}
            <span className="bg-gradient-to-r from-fuchsia-400 via-amber-400 to-indigo-400 bg-clip-text text-transparent">
              {cityName}
            </span>
            .
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
            Curated halls, breweries, vineyards, lofts and rooftop bars — every venue with active events on Eventism, sorted by upcoming activity.
          </p>
          {!loading && (
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-1" data-testid="city-venues-stats">
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-indigo-400" />
                <strong className="text-foreground">{totalActive}</strong> active {totalActive === 1 ? 'venue' : 'venues'}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-amber-400" />
                <strong className="text-foreground">{totalUpcoming}</strong> upcoming {totalUpcoming === 1 ? 'event' : 'events'} citywide
              </span>
            </div>
          )}
          {/* Active filter chip — only renders when a venue-profile link
              brought the user here with `?type=…`, `?neighborhood=…`,
              or `?tag=…`. Clearing returns to the full city directory. */}
          {(typeFilter || neighborhoodFilter || tagFilter) && (
            <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="city-venues-active-filter">
              <span className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
                Filtered by
              </span>
              {typeFilter && (
                <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/30 gap-1.5 py-1 pl-2.5 pr-1">
                  {capitalize(typeFilter)}
                  <Link
                    to={`/${city}/venues`}
                    aria-label="Clear filter"
                    className="ml-0.5 rounded-full hover:bg-fuchsia-500/30 p-0.5"
                    data-testid="clear-filter-type"
                  ><X className="h-3 w-3" /></Link>
                </Badge>
              )}
              {neighborhoodFilter && (
                <Badge className="bg-rose-500/15 text-rose-300 border border-rose-400/30 gap-1.5 py-1 pl-2.5 pr-1">
                  {capitalize(neighborhoodFilter)}
                  <Link
                    to={`/${city}/venues`}
                    aria-label="Clear filter"
                    className="ml-0.5 rounded-full hover:bg-rose-500/30 p-0.5"
                    data-testid="clear-filter-neighborhood"
                  ><X className="h-3 w-3" /></Link>
                </Badge>
              )}
              {tagFilter && (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-400/30 gap-1.5 py-1 pl-2.5 pr-1">
                  {capitalize(tagFilter)}
                  <Link
                    to={`/${city}/venues`}
                    aria-label="Clear filter"
                    className="ml-0.5 rounded-full hover:bg-amber-500/30 p-0.5"
                    data-testid="clear-filter-tag"
                  ><X className="h-3 w-3" /></Link>
                </Badge>
              )}
            </div>
          )}
        </section>

        {/* Grid */}
        {loading ? (
          <div className="py-20 flex justify-center" data-testid="city-venues-loading">
            <Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" />
          </div>
        ) : venues.length === 0 ? (
          <Card className="text-center py-12 border-dashed" data-testid="city-venues-empty">
            <CardContent>
              <Building2 className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                No active venues in {cityName} yet — check back soon.
              </p>
              <Link to={`/events/${city}`} className="text-sm text-fuchsia-400 hover:underline">
                Browse {cityName} events instead
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-venues-grid">
            {venues.map((v) => (
              <VenueCard key={v.slug || v.venue_id} venue={v} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VenueCard({ venue: v }) {
  return (
    <Link
      to={v.url}
      className="group relative rounded-xl overflow-hidden border border-border hover:border-fuchsia-400/50 transition-colors bg-card flex flex-col"
      data-testid={`venue-card-${v.slug}`}
    >
      <div className="relative aspect-[16/10] bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 overflow-hidden">
        {v.cover_photo ? (
          <img
            src={v.cover_photo}
            alt={v.name}
            loading="lazy"
            width={640}
            height={400}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Building2 className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {v.is_featured && (
          <Badge className="absolute top-3 left-3 bg-amber-500 text-black border-0 text-[10px]">
            <Sparkles className="h-3 w-3 mr-1" /> Featured
          </Badge>
        )}
        {v.upcoming_events_count > 0 && (
          <Badge className="absolute top-3 right-3 bg-fuchsia-500/90 border-0 text-[10px]" data-testid={`venue-upcoming-${v.slug}`}>
            {v.upcoming_events_count} upcoming
          </Badge>
        )}
      </div>
      <div className="p-4 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base leading-tight group-hover:text-fuchsia-400 transition-colors line-clamp-2">
            {v.name}
          </h3>
        </div>
        {v.address && (
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{v.address}{v.city ? `, ${v.city}` : ''}</span>
          </p>
        )}
        {(v.capacity_min || v.capacity_max) && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {v.capacity_min && v.capacity_max
              ? `Capacity ${v.capacity_min}–${v.capacity_max}`
              : `Up to ${v.capacity_max || v.capacity_min} guests`}
          </p>
        )}
        {v.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 pt-1">{v.description}</p>
        )}
      </div>
    </Link>
  );
}
